import { useState, useEffect, useCallback } from 'react';
import {
  AlertRecipient,
  NotificationBulkScope,
  shipmentAlertRecipient,
} from '../apis/shipment';
import useUnauthorize from './useUnauthorize';
import { useToken } from '../common/helper';

interface UseFetchAlertRecipientsReturn {
  recipientData?: AlertRecipient[];
  refetchAlertRecipients: () => void;
}

/**
 * Hook to fetch alert recipients for a specific shipment
 *
 * @param shipmentId - The ID of the shipment
 * @returns Object containing recipient data and refetch function
 */
const useFetchAlertRecipients = (
  shipmentId: string | string[],
  isMultiple: boolean,
  bulkScope: NotificationBulkScope | null = null
): UseFetchAlertRecipientsReturn => {
  const { checkTokenExpiry } = useToken();
  const { unauthorize } = useUnauthorize();
  const [recipientData, setRecipientData] = useState<
    AlertRecipient[] | undefined
  >(undefined);

  const fetchRecipientData = useCallback((): void => {
    const token = checkTokenExpiry();
    if (!token) {
      return;
    }
    const shipmentIds = Array.isArray(shipmentId) ? shipmentId : [shipmentId];
    shipmentAlertRecipient(
      token,
      bulkScope ??
        (isMultiple
          ? { shipment_ids: shipmentIds, bulk: true }
          : { shipment_ids: shipmentIds })
    )
      .then((res) => {
        const data = res?.data?.data ? res?.data?.data : null;
        if (data?.is_success) {
          setRecipientData(data?.data ?? undefined);
        } else {
          setRecipientData(undefined);
        }
      })
      .catch((error) => {
        unauthorize(error);
        setRecipientData(undefined);
      });
  }, [shipmentId, checkTokenExpiry, unauthorize, isMultiple, bulkScope]);

  useEffect(() => {
    fetchRecipientData();
  }, [fetchRecipientData]);

  return { recipientData, refetchAlertRecipients: fetchRecipientData };
};

export default useFetchAlertRecipients;
